import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, ExternalLink } from "lucide-react";
import { useContent } from "../../context/ContentContext";
import { Reveal } from "./Reveal";
import { galleryCover } from "../../lib/media";
import { FramedImage } from "./FramedImage";
import { SectionBg } from "./SectionBg";

export const Realisations = () => {
  const { content } = useContent();
  const navigate = useNavigate();
  const meta = content.realisationsMeta || {};
  const all = (content.realisations || []).filter((p) => (p.status || "published") !== "hidden");
  const [active, setActive] = useState(null);
  const [filter, setFilter] = useState("all");

  // Category tabs: managed categories (non-hidden) that are actually used, in admin order.
  const cats = useMemo(() => {
    const used = new Set(all.map((p) => p.cat).filter(Boolean));
    const managed = (meta.categories || []).filter((c) => !c.hidden && used.has(c.label)).map((c) => c.label);
    const extras = [...used].filter((c) => !managed.includes(c));
    return [...managed, ...extras];
  }, [all, meta.categories]);

  const items = filter === "all" ? all : all.filter((p) => p.cat === filter);

  const openItem = (p) => {
    if (p.linkType === "external" && p.externalUrl) window.open(p.externalUrl, "_blank", "noopener");
    else navigate(`/projet/${p.slug}`);
  };

  return (
    <section id="realisations" className="relative overflow-hidden bg-surface py-24 md:py-40 border-t border-white/10" data-testid="realisations-section">
      <SectionBg id="realisations" />
      <div className="relative z-10 max-w-[1500px] mx-auto px-6 md:px-12">
        <Reveal className="mb-12 md:mb-16">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-electric" />
            <span className="text-xs tracking-[0.35em] uppercase text-electric">{meta.eyebrow || "Nos réalisations"}</span>
          </div>
          <h2 className="font-serif text-5xl md:text-7xl text-white tracking-tight leading-[0.95]">
            Des œuvres qui <span className="italic text-electric">respirent.</span>
          </h2>
        </Reveal>

        {cats.length > 0 && (
          <Reveal className="mb-10 md:mb-14">
            <div className="flex flex-wrap gap-2.5" data-testid="realisation-filters">
              <button onClick={() => setFilter("all")} data-testid="realisation-filter-all"
                className={`px-4 py-2 rounded-full text-[11px] tracking-[0.15em] uppercase border transition-colors ${filter === "all" ? "bg-electric text-black border-electric" : "border-white/15 text-secondary2 hover:border-electric hover:text-electric"}`}>Tout</button>
              {cats.map((c) => (
                <button key={c} onClick={() => setFilter(c)} data-testid={`realisation-filter-${c}`}
                  className={`px-4 py-2 rounded-full text-[11px] tracking-[0.15em] uppercase border transition-colors ${filter === c ? "bg-electric text-black border-electric" : "border-white/15 text-secondary2 hover:border-electric hover:text-electric"}`}>{c}</button>
              ))}
            </div>
          </Reveal>
        )}

        <div className="grid grid-cols-1 md:grid-cols-12 grid-flow-row-dense gap-4 md:gap-6" onMouseLeave={() => setActive(null)}>
          {items.map((p, i) => (
            <Reveal key={p.id || p.slug || i} delay={(i % 2) * 0.1} className={`${p.span || "md:col-span-6"} col-span-1`}>
              <motion.div
                data-testid={`realisation-item-${i}`}
                onMouseEnter={() => setActive(i)}
                onClick={() => openItem(p)}
                className="group relative overflow-hidden rounded-lg h-[52vw] md:h-[30rem] cursor-pointer"
                animate={{ opacity: active === null || active === i ? 1 : 0.35 }}
                transition={{ duration: 0.5 }}
              >
                <div className="w-full h-full transition-transform duration-[1.2s] ease-out group-hover:scale-110">
                  <FramedImage src={p.img || galleryCover(p.gallery)} frame={p.imgFrame} alt={p.title} />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/80 via-[#050505]/10 to-transparent" />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <div className="w-16 h-16 rounded-full border border-electric/70 backdrop-blur-sm flex items-center justify-center bg-black/30">
                    {p.linkType === "external" && p.externalUrl ? <ExternalLink size={20} className="text-electric" /> : <Play size={20} className="fill-electric text-electric ml-0.5" />}
                  </div>
                </div>
                <div className="absolute bottom-0 inset-x-0 p-6 md:p-8">
                  <div className="translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-electric mb-2">{p.cat}{(p.info || {}).year ? ` · ${p.info.year}` : ""}</div>
                    <h3 className="font-serif text-3xl md:text-4xl text-white">{p.title}</h3>
                  </div>
                </div>
                <div className="absolute top-6 left-6 h-px w-0 bg-electric group-hover:w-12 transition-all duration-700" />
              </motion.div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
